import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Deadlines } from './components/Deadlines/Deadlines';
import {
    getCheckedEvaluationTypes,
    getVisibleDeadlineMilestones,
    isManagerAssessmentMilestone,
    numberVisibleDeadlineMilestones,
} from './milestones';
import { useAutosaveDeadlineDates } from './useAutosaveDeadlineDates';
import { useDeadlineRecurrence } from './useDeadlineRecurrence';
import { useHydrateDeadlineValues } from './useHydrateDeadlineValues';
import { useSaveDeadlines } from './useSaveDeadlines';
import { getDeadlineFieldErrors, getRecurrenceError } from './validation';

import type { DeadlinesRollback } from './deadlineRollback';
import type { FrequencyValue } from '@/constants/Frequency';
import type { CycleEvaluationGroupFormNavigatorParams } from '@/navigation/CycleEvaluation/CycleEvaluationGroupFormNavigator';

import { useGetCycleEvaluation } from '@/api/cycle-evaluation-services/cycle-evaluation';
import { useGetCycleEvaluationGroup } from '@/api/cycle-evaluation-services/cycle-evaluation-group';
import { useGetCycleMilestonesByCycle } from '@/api/cycle-evaluation-services/cycle-milestone';
import { deadlinesOnNextAtom } from '@/atoms/cycleEvaluation';
import { EvaluationType } from '@/constants/cycle-evaluation/CycleEvaluation';
import { useAmelioTranslations, useRegisterPageAction } from '@/hooks';
import { useFetchErrorsHandler } from '@/hooks/api/useFetchErrorsHandler';
import { useManagerAssessmentAccess } from '@/hooks/performance-tier/useManagerAssessmentAccess';
import { isCycleEvaluationGroupLaunched } from '@/navigation/CycleEvaluation/cycleEvaluationGroupLaunched';
import { isCycleEvaluationGroupReadOnly } from '@/navigation/CycleEvaluation/cycleEvaluationGroupReadOnly';
import { findGroupEvaluationByType } from '@/navigation/CycleEvaluation/cycleEvaluationGroupSteps';

/**
 * Deadlines step: four to seven milestones depending on the selected evaluation types.
 * Orphans persist via CycleMilestone, launches via the existing CycleSchedule.
 */
const DeadlinesPage = () => {
    const { t } = useAmelioTranslations();
    const { cycleEvaluationGroupId } = useParams<CycleEvaluationGroupFormNavigatorParams>();

    const {
        data: group,
        isError: isGroupError,
        isPending: isGroupPending,
    } = useGetCycleEvaluationGroup(cycleEvaluationGroupId);

    const {
        data: orphanMilestones = [],
        isError: isMilestonesError,
        isPending: isMilestonesPending,
    } = useGetCycleMilestonesByCycle(cycleEvaluationGroupId);

    const checkedTypes = useMemo(() => getCheckedEvaluationTypes(group), [group]);

    const selfEvaluationChild = findGroupEvaluationByType(group, EvaluationType.SELF_EVALUATION);
    const feedback360Child = findGroupEvaluationByType(group, EvaluationType.EVALUATION_360);
    const managerEvaluationChild = findGroupEvaluationByType(
        group,
        EvaluationType.MANAGER_EVALUATION,
    );

    const selfEvaluationQuery = useGetCycleEvaluation(selfEvaluationChild?.id ?? '');
    const feedback360Query = useGetCycleEvaluation(feedback360Child?.id ?? '');
    const managerEvaluationQuery = useGetCycleEvaluation(managerEvaluationChild?.id ?? '');

    const cycleEvaluationQueryByType = useMemo(
        () => ({
            [EvaluationType.SELF_EVALUATION]: selfEvaluationQuery,
            [EvaluationType.EVALUATION_360]: feedback360Query,
            [EvaluationType.MANAGER_EVALUATION]: managerEvaluationQuery,
        }),
        [selfEvaluationQuery, feedback360Query, managerEvaluationQuery],
    );

    const managerAssessment = useManagerAssessmentAccess(group);
    const visibleMilestones = useMemo(
        () => getVisibleDeadlineMilestones(checkedTypes, managerAssessment.isShown),
        [checkedTypes, managerAssessment.isShown],
    );
    // Read-only Manager Assessment milestones are shown, never checked: the reader cannot change
    // them, so they can never block the step. Untouched, they are never written either.
    const editableMilestones = useMemo(
        () =>
            managerAssessment.isReadOnly
                ? visibleMilestones.filter(
                      (milestone) => !isManagerAssessmentMilestone(milestone.id),
                  )
                : visibleMilestones,
        [visibleMilestones, managerAssessment.isReadOnly],
    );
    const numberedMilestones = useMemo(
        () => numberVisibleDeadlineMilestones(visibleMilestones),
        [visibleMilestones],
    );

    const cycleEvaluationQueries = checkedTypes.map((type) => cycleEvaluationQueryByType[type]);
    const cycleEvaluationErrors = cycleEvaluationQueries.map((query) => !!query?.isError);

    // A refusal ends the wait: that child will never produce data, and going on waiting leaves the
    // skeleton up forever with nothing but the error handler to speak for it.
    const isDataReady =
        !!group &&
        !isMilestonesPending &&
        cycleEvaluationQueries.every((query) => !!query?.data || !!query?.isError);

    const {
        values,
        remindersById,
        hasUserEditedRef,
        hasUserEdited,
        markDraftChanged,
        handleScheduledAtChange,
        handleDueAtChange,
        handleRemindersChange,
        rollbackRefusedValues,
    } = useHydrateDeadlineValues({
        isDataReady,
        orphanMilestones,
        checkedTypes,
        cycleEvaluationQueryByType,
    });

    const { frequency, setFrequency } = useDeadlineRecurrence(group);

    const handleFrequencyChange = useCallback(
        (pickedFrequency: FrequencyValue) => {
            markDraftChanged();
            setFrequency(pickedFrequency);
        },
        [markDraftChanged, setFrequency],
    );

    const isReadOnly = isCycleEvaluationGroupReadOnly(group);
    const isLaunched = isCycleEvaluationGroupLaunched(group);

    // A past date refuses the step while the cycle is built, and again once a launched cycle is
    // edited; consulted untouched, a launched cycle only shows it as a warning.
    const isPastDateBlocking = !isLaunched || hasUserEdited;

    const errors = useMemo(
        () =>
            getDeadlineFieldErrors({
                visibleMilestones: editableMilestones,
                values,
                isPastDateBlocking,
            }),
        [editableMilestones, values, isPastDateBlocking],
    );

    const recurrenceError = getRecurrenceError(frequency);

    const saveDeadlines = useSaveDeadlines({
        isReadOnly,
        isLaunched,
        hasUserEdited,
        errors,
        group,
        cycleEvaluationGroupId,
        values,
        orphanMilestones,
        checkedTypes,
        cycleEvaluationQueryByType,
        remindersById,
        frequency,
    });

    const [isSavingOnNext, setIsSavingOnNext] = useState(false);

    const handleWritesRefused = useCallback(
        (rollback: DeadlinesRollback) => {
            rollbackRefusedValues(rollback);

            if (rollback.hasRefusedRecurrence) {
                setFrequency(rollback.frequency);
            }
        },
        [rollbackRefusedValues, setFrequency],
    );

    const { markDraftSaved } = useAutosaveDeadlineDates({
        isReadOnly,
        hasUserEditedRef,
        cycleEvaluationGroupId,
        group,
        values,
        orphanMilestones,
        checkedTypes,
        cycleEvaluationQueryByType,
        remindersById,
        frequency,
        isSavingOnNext,
        onWritesRefused: handleWritesRefused,
    });

    const onNext = useCallback(async () => {
        setIsSavingOnNext(true);

        try {
            const isSaved = await saveDeadlines();

            if (isSaved) {
                markDraftSaved({ values, remindersById, frequency });
            }

            return isSaved;
        } finally {
            setIsSavingOnNext(false);
        }
    }, [saveDeadlines, markDraftSaved, values, remindersById, frequency]);

    useRegisterPageAction(deadlinesOnNextAtom, onNext);

    useFetchErrorsHandler({ errors: [isGroupError, isMilestonesError, ...cycleEvaluationErrors] });

    return (
        <Deadlines
            isLoading={!isDataReady || isGroupPending}
            isReadOnly={isReadOnly}
            isManagerAssessmentReadOnly={managerAssessment.isReadOnly}
            numberedMilestones={numberedMilestones}
            values={values}
            remindersById={remindersById}
            errors={errors}
            frequency={frequency}
            recurrenceErrorMessage={recurrenceError ? t(recurrenceError.key) : undefined}
            onScheduledAtChange={handleScheduledAtChange}
            onDueAtChange={handleDueAtChange}
            onRemindersChange={handleRemindersChange}
            onFrequencyChange={handleFrequencyChange}
        />
    );
};

export { DeadlinesPage };
